import { createFileRoute } from '@tanstack/react-router'
import { useState, useCallback, useMemo } from 'react'
import {
  useAssets,
  useUpdateAssetClassification,
  useResetClassification,
  useAssetStats,
} from '@/hooks/api/useAssets'
import { AssetClassificationTable } from '@/components/assets/AssetClassificationTable'
import { AssetDetailPanel } from '@/components/assets/AssetDetailPanel'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Search,
  Filter,
  LayoutGrid,
  Coins,
  Building2,
  AlertCircle,
  Sparkles,
  Download,
  RefreshCw,
} from 'lucide-react'
import { AssetWithClassification, AssetFilterType, isNewAsset } from '@/types/assets'
import { AssetClass } from '@/types/api'
import { cn } from '@/lib/utils'

/**
 * Assets Page - Asset Classification Management
 *
 * Features:
 * - View all assets with Crypto/TradFi classifications
 * - Override classifications with visual feedback
 * - Filter by asset class (All, Crypto, TradFi, Overrides, New)
 * - Search by ticker or name
 * - Detailed asset view with proxy information
 * - Classification persistence (backend storage)
 *
 * Per UI-03: Asset classification screen with Crypto vs TradFi columns,
 * toggle override capability, and persistence.
 */
function AssetsPage() {
  // State
  const [filter, setFilter] = useState<AssetFilterType>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedAsset, setSelectedAsset] = useState<AssetWithClassification | null>(null)

  // Data hooks
  const { data: assets = [], isLoading, error, refetch } = useAssets()
  const stats = useAssetStats()
  const updateMutation = useUpdateAssetClassification()
  const resetMutation = useResetClassification()

  // Filtered assets based on current filter and search
  const filteredAssets = useMemo(() => {
    let result = assets

    // Apply filter type
    switch (filter) {
      case 'crypto':
        result = result.filter((a) => a.assetClass === 'CRYPTO')
        break
      case 'tradfi':
        result = result.filter((a) => a.assetClass === 'TRADFI')
        break
      case 'overrides':
        result = result.filter((a) => a.isOverride)
        break
      case 'new':
        result = result.filter((a) =>
          isNewAsset(a.metadata?.listingDate || a.listingDate)
        )
        break
      case 'all':
      default:
        break
    }

    // Apply search
    if (searchQuery.trim()) {
      const term = searchQuery.toLowerCase().trim()
      result = result.filter(
        (asset) =>
          asset.symbol.toLowerCase().includes(term) ||
          asset.name.toLowerCase().includes(term)
      )
    }

    return result
  }, [assets, filter, searchQuery])

  // Event handlers
  const handleClassificationChange = useCallback(
    (assetId: string, classification: AssetClass, isOverride: boolean) => {
      updateMutation.mutate({ assetId, classification, isOverride })
    },
    [updateMutation]
  )

  const handleResetToDefault = useCallback(
    (assetId: string) => {
      resetMutation.mutate({ assetId })
    },
    [resetMutation]
  )

  const handleViewDetails = useCallback((asset: AssetWithClassification) => {
    setSelectedAsset(asset)
  }, [])

  const handleClosePanel = useCallback(() => {
    setSelectedAsset(null)
  }, [])

  // Filter button configuration
  const filterButtons: { id: AssetFilterType; label: string; count: number; icon: typeof Coins }[] = [
    { id: 'all', label: 'All Assets', count: stats.total, icon: LayoutGrid },
    { id: 'crypto', label: 'Crypto', count: stats.cryptoCount, icon: Coins },
    { id: 'tradfi', label: 'TradFi', count: stats.tradfiCount, icon: Building2 },
    { id: 'overrides', label: 'Overrides', count: stats.overrideCount, icon: AlertCircle },
    { id: 'new', label: 'New', count: stats.newCount, icon: Sparkles },
  ]

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Asset Classification</h2>
          <p className="mt-1 text-gray-600">
            Review and override asset classifications for stress testing
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={cn('mr-2 h-4 w-4', isLoading && 'animate-spin')} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" disabled>
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {filterButtons.map(({ id, label, count, icon: Icon }) => (
          <Card
            key={id}
            className={cn(
              'cursor-pointer transition-colors',
              filter === id ? 'border-primary bg-primary/5' : 'hover:bg-gray-50'
            )}
            onClick={() => setFilter(id)}
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Icon className="h-4 w-4" />
                {label}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{count}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search and Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by ticker or name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="px-3 py-1">
            <Filter className="mr-2 h-3 w-3" />
            {filteredAssets.length} of {assets.length} assets
          </Badge>
        </div>
      </div>

      {/* Active Filter Display */}
      {filter !== 'all' && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Active filter:</span>
          <Badge
            variant="secondary"
            className="cursor-pointer hover:bg-gray-200"
            onClick={() => setFilter('all')}
          >
            {filterButtons.find((b) => b.id === filter)?.label}
            <span className="ml-1">×</span>
          </Badge>
        </div>
      )}

      {/* Asset Table */}
      <AssetClassificationTable
        assets={filteredAssets}
        isLoading={isLoading}
        onClassificationChange={handleClassificationChange}
        onResetToDefault={handleResetToDefault}
        onViewDetails={handleViewDetails}
        searchQuery={searchQuery}
        error={error}
      />

      {/* Detail Panel */}
      <AssetDetailPanel
        asset={selectedAsset}
        onClose={handleClosePanel}
        onClassificationChange={handleClassificationChange}
        onResetToDefault={handleResetToDefault}
      />
    </div>
  )
}

export const Route = createFileRoute('/assets')({
  component: AssetsPage,
})
